import { supabase } from '../lib/supabase';
import { withOperationTimeout, withSupabaseQueryTimeout } from '../lib/supabaseTimeout';

const DOCUMENT_LIBRARY_BASE_COLUMNS = [
  'id',
  'hoa_id',
  'title',
  'file_name',
  'file_url',
  'file_size_bytes',
  'mime_type',
  'category',
  'confidentiality_level',
  'validation_status',
  'validation_level',
  'ai_status',
  'is_indexed',
  'uploaded_by',
  'description',
  'phase_applicability',
  'visible_to_homeowners',
  'created_at',
  'updated_at',
];
const DOCUMENT_LIBRARY_EXTENDED_COLUMNS = [
  ...DOCUMENT_LIBRARY_BASE_COLUMNS,
  'folder_id',
  'display_order',
];
const DOCUMENT_LIBRARY_COLUMNS = DOCUMENT_LIBRARY_EXTENDED_COLUMNS.join(',');
const DOCUMENT_LIBRARY_FALLBACK_COLUMNS = DOCUMENT_LIBRARY_BASE_COLUMNS.join(',');
const DOCUMENT_LINK_TIMEOUT_MS = 45000;
const DOCUMENT_LINK_RETRY_DELAY_MS = 700;

const isMissingSchemaError = (error) => {
  const message = String(error?.message || '').toLowerCase();
  return error?.code === '42P01' ||
    error?.code === '42703' ||
    error?.code === 'PGRST205' ||
    message.includes('schema cache') ||
    message.includes('does not exist') ||
    message.includes('could not find the table') ||
    message.includes('could not find the') && message.includes('column');
};

const stripOrderingFields = (updates) => {
  const next = { ...updates };
  delete next.folder_id;
  delete next.display_order;
  return next;
};

const sortLibraryDocuments = (docs = []) => {
  return [...docs].sort((a, b) => {
    const aManual = a.display_order !== null && a.display_order !== undefined;
    const bManual = b.display_order !== null && b.display_order !== undefined;
    if (aManual && bManual) return a.display_order - b.display_order;
    if (aManual !== bManual) return aManual ? 1 : -1;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
};

const normalizeStoragePath = (filePath) => {
  if (!filePath || filePath === 'pending') return null;

  if (!/^https?:\/\//i.test(filePath)) return filePath.replace(/^\/+/, '');

  try {
    const url = new URL(filePath);
    const marker = '/storage/v1/object/';
    const markerIndex = url.pathname.indexOf(marker);
    if (markerIndex === -1) return filePath;

    const objectPath = url.pathname.slice(markerIndex + marker.length);
    const withoutAccessType = objectPath.replace(/^(public|sign|authenticated)\//, '');
    const bucketPrefix = 'documents/';
    if (!withoutAccessType.startsWith(bucketPrefix)) return filePath;

    return decodeURIComponent(withoutAccessType.slice(bucketPrefix.length));
  } catch (error) {
    return filePath;
  }
};

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const withStorageRetry = async (operationFactory, attempts = 2) => {
  let lastError = null;

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await operationFactory();
    } catch (error) {
      lastError = error;
      if (attempt < attempts - 1) {
        await wait(DOCUMENT_LINK_RETRY_DELAY_MS * (attempt + 1));
      }
    }
  }

  throw lastError;
};

// TODO(pre-launch security): tighten Supabase storage.objects policies for the
// documents bucket. Remote policies currently permit anon read/upload; require
// authenticated access scoped to HOA/role before Wednesday launch.

// ── PDF TEXT EXTRACTION ──────────────────────────────────────
// extract-document-text authenticates the caller (admins only), so it needs the
// signed-in user's access token — the public anon key is rejected with 401.
export const getExtractionAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Extraction service error: sign in again to upload documents');
  }
  return { Authorization: `Bearer ${session.access_token}` };
};

// Delegates to Supabase Edge Function — eliminates pdf.js CDN/worker conflicts
// Returns { text, wordCount, pageCount, method, quality }
export const extractTextFromPDF = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file, file.name);

    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/extract-document-text`,
      {
        method: 'POST',
        headers: await getExtractionAuthHeaders(),
        body: formData,
      }
    );

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Extraction service error: ${response.status} — ${err}`);
    }

    const result = await response.json();

    return {
      text: result.text || '',
      wordCount: result.wordCount || 0,
      pageCount: result.pageCount || 0,
      method: result.method || 'pdf_server',
      quality: result.quality || 'failed',
    };
  } catch (err) {
    console.error('PDF extraction error:', err);
    return {
      text: '',
      wordCount: 0,
      pageCount: 0,
      method: 'pdf_server',
      quality: 'failed',
      note: err.message,
    };
  }
};

// ── AI DOCUMENT READING ──────────────────────────────────────
// pdf-parse (above) only copies a PDF's embedded text layer, which is garbage
// on scanned reports. The AI readers (LlamaParse, with a Claude vision backup)
// live in services/documentReader.js.
export const isPdfDocument = (doc) =>
  doc?.mime_type === 'application/pdf' ||
  doc?.type === 'application/pdf' ||
  /\.pdf$/i.test(doc?.file_name || doc?.name || '');

// ── DOCUMENT STORAGE SERVICE ─────────────────────────────────
class DocumentStorageService {
  // Every caller passes the current association's id (useHOA). There is no
  // default: a missing id used to fall back to Crestview's, which would have filed
  // another association's documents under Crestview.
  getHoaId(hoaId) {
    if (!hoaId) throw new Error('documentStorage: hoaId is required');
    return hoaId;
  }

  // Save document: extract text → upload to Storage → insert row
  // documentData: { name, category, visibility, uploadedBy (uuid) }
  // fileBlob: the raw File object from the upload input
  // extractionResult: result from extractTextFromPDF (may be pre-computed)
  // options.skipIndexing: caller will run the ingestion pipeline itself once it
  //   has better text (e.g. after layout parsing) — avoids chunking pdf-parse
  //   garbage and racing a second pipeline run on the same document.
  async saveDocument(documentData, fileBlob, extractionResult = null, options = {}) {
    try {
      const hoaId = this.getHoaId(documentData.hoaId);
      // 0. Guard: if a document with this exact filename already exists, return it
      //    rather than inserting a duplicate. This prevents stacking rows when the
      //    user re-uploads the same file across multiple modal sessions.
      const { data: existing } = await supabase
        .from('documents')
        .select('*')
        .eq('hoa_id', hoaId)
        .eq('file_name', fileBlob.name)
        .maybeSingle();
      if (existing) {
        console.warn(`[documentStorage] Duplicate upload blocked: "${fileBlob.name}" already exists (id: ${existing.id})`);
        return existing;
      }

      // 1. Extract text if not already done
      const extraction = extractionResult || await extractTextFromPDF(fileBlob);
      
      // 2. Build storage path: hoa_id/timestamp_filename
      const timestamp = Date.now();
      const safeName = fileBlob.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${hoaId}/${timestamp}_${safeName}`;
      
      // 3. Upload file to Supabase Storage bucket 'documents'
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, fileBlob, {
          contentType: fileBlob.type || 'application/pdf',
          upsert: false,
        });
      
      if (uploadError) throw new Error(`Storage upload failed: ${uploadError.message}`);
      
      // 4. Get public URL (signed URL since bucket is private)
      const { data: urlData } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 60 * 60 * 24 * 365); // 1 year
      
      // 5. Normalize confidentiality level: UI uses 'members', DB constraint expects 'member'
      const VALID_CONFIDENTIALITY = ['public', 'member', 'board', 'admin'];
      let confLevel = (documentData.visibility || 'member').replace(/s$/, ''); // 'members' → 'member'
      if (!VALID_CONFIDENTIALITY.includes(confLevel)) confLevel = 'member';

      // 6. Insert metadata row into documents table
      const insertPayload = {
        hoa_id: hoaId,
        title: documentData.name || fileBlob.name,
        file_name: fileBlob.name,
        file_url: storagePath,
        file_size_bytes: fileBlob.size,
        mime_type: fileBlob.type || 'application/pdf',
        category: documentData.category,
        confidentiality_level: confLevel,
        validation_status: 'pending',
        validation_level: 0,
        extracted_text: extraction.text || null,
        is_indexed: false,
        uploaded_by: documentData.uploadedBy || null,
        description: null,
        phase_applicability: 'both',
        visible_to_homeowners: documentData.visibleToHomeowners === true,
        folder_id: documentData.folderId || null,
        display_order: null,
      };
      const { data: inserted, error: insertError } = await supabase
        .from('documents')
        .insert(insertPayload)
        .select()
        .single();
      
      let insertedDocument = inserted;
      if (insertError && isMissingSchemaError(insertError)) {
        const { data: fallbackInserted, error: fallbackInsertError } = await supabase
          .from('documents')
          .insert(stripOrderingFields(insertPayload))
          .select()
          .single();
        if (fallbackInsertError) throw new Error(`Database insert failed: ${fallbackInsertError.message}`);
        insertedDocument = fallbackInserted;
      } else if (insertError) {
        throw new Error(`Database insert failed: ${insertError.message}`);
      }
      
      // Kick off chunk indexing (fire-and-forget — upload never fails because indexing fails)
      if (!options.skipIndexing) import('../rag/ingestionPipeline').then(({ runIngestionPipeline }) => {
        if (extraction.text && extraction.text.trim().length >= 50) {
          runIngestionPipeline(supabase, insertedDocument.id, hoaId).then(null, err =>
            console.error('[documentStorage] Ingestion pipeline error:', err.message)
          );
        }
      });

      return {
        ...insertedDocument,
        signed_url: urlData?.signedUrl || null,
        extraction_result: extraction,
      };
      
    } catch (error) {
      console.error('saveDocument error:', error);
      throw error;
    }
  }

  // Get all documents metadata for this HOA
  async getAllDocumentsMetadata({ throwOnError = false, hoaId: requestedHoaId } = {}) {
    try {
      const hoaId = this.getHoaId(requestedHoaId);
      const { data, error } = await withSupabaseQueryTimeout(
        () => supabase
          .from('documents')
          .select('*')
          .eq('hoa_id', hoaId)
          .order('created_at', { ascending: false }),
        'Loading documents',
        30000
      );
      
      if (error) throw error;
      
      // Return as object keyed by id (matches existing RAG engine expectations)
      const result = {};
      sortLibraryDocuments(data || []).forEach(doc => {
        result[doc.id] = {
          ...doc,
          name: doc.title,  // alias for RAG engine
          // ai_status is its own DB column — do NOT alias from validation_status
        };
      });
      return result;
      
    } catch (error) {
      console.error('getAllDocumentsMetadata error:', error);
      if (throwOnError) throw error;
      return {};
    }
  }

  // Get all documents as array
  async getAllDocuments(options = {}) {
    const metadataObj = await this.getAllDocumentsMetadata(options);
    return Object.values(metadataObj);
  }

  formatDocumentForDisplay(doc, fileUrl = null) {
    return {
      ...doc,
      name: doc.title,
      size: doc.file_size_bytes ? (doc.file_size_bytes / (1024 * 1024)).toFixed(2) + ' MB' : '0 MB',
      type: doc.mime_type === 'application/pdf' ? 'PDF' : doc.mime_type?.startsWith('image/') ? 'Image' : 'Document',
      content: fileUrl,
      has_file: Boolean(doc.file_url && doc.file_url !== 'pending'),
    };
  }

  // Lightweight library rows: excludes extracted_text so browsing does not download RAG payloads.
  async getDocumentLibraryItems({ throwOnError = false, hoaId: requestedHoaId, visibleToHomeownersOnly = false } = {}) {
    try {
      const hoaId = this.getHoaId(requestedHoaId);
      const buildLibraryQuery = (columns) => {
        let query = supabase
          .from('documents')
          .select(columns)
          .eq('hoa_id', hoaId)
          .order('created_at', { ascending: false });

        if (visibleToHomeownersOnly) {
          query = query.eq('visible_to_homeowners', true);
        }

        return query;
      };

      let { data, error } = await withSupabaseQueryTimeout(
        () => buildLibraryQuery(DOCUMENT_LIBRARY_COLUMNS),
        'Loading documents',
        30000
      );
      if (error && isMissingSchemaError(error)) {
        const fallback = await withSupabaseQueryTimeout(
          () => buildLibraryQuery(DOCUMENT_LIBRARY_FALLBACK_COLUMNS),
          'Loading documents',
          30000
        );
        data = fallback.data;
        error = fallback.error;
      }

      if (error) throw error;

      return sortLibraryDocuments(data || []).map(doc => this.formatDocumentForDisplay(doc, null));
    } catch (error) {
      console.error('getDocumentLibraryItems error:', error);
      if (throwOnError) throw error;
      return [];
    }
  }

  async createSignedUrlForPath(filePath, expiresIn = 3600) {
    const storagePath = normalizeStoragePath(filePath);
    if (!storagePath) return null;

    if (/^https?:\/\//i.test(storagePath)) return storagePath;

    const { data: urlData, error } = await withStorageRetry(() => withOperationTimeout(
      supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, expiresIn),
      'Preparing document link',
      DOCUMENT_LINK_TIMEOUT_MS
    ));

    if (error) throw error;
    return urlData?.signedUrl || null;
  }

  async getSignedUrlsForDocuments(documents = [], expiresIn = 3600) {
    const signedByDocumentId = new Map();
    const pathDocs = [];

    documents.forEach(doc => {
      const storagePath = normalizeStoragePath(doc.file_url);
      if (!storagePath) return;

      if (/^https?:\/\//i.test(storagePath)) {
        signedByDocumentId.set(doc.id, storagePath);
        return;
      }

      pathDocs.push({ id: doc.id, storagePath });
    });

    if (pathDocs.length === 0) return signedByDocumentId;

    try {
      const { data: signedUrls, error } = await withStorageRetry(() => withOperationTimeout(
        supabase.storage
          .from('documents')
          .createSignedUrls(pathDocs.map(doc => doc.storagePath), expiresIn),
        'Preparing document links',
        DOCUMENT_LINK_TIMEOUT_MS
      ));

      if (error) throw error;

      const signedByPath = new Map();
      (signedUrls || []).forEach(item => {
        if (item.path) signedByPath.set(item.path, item.signedUrl || null);
      });

      pathDocs.forEach(doc => {
        const signedUrl = signedByPath.get(doc.storagePath);
        if (signedUrl) signedByDocumentId.set(doc.id, signedUrl);
      });
    } catch (error) {
      console.warn('Document link prewarm failed:', error?.message || error);
    }

    return signedByDocumentId;
  }

  // Get all documents with signed download URLs
  async getAllDocumentsWithFiles(options = {}) {
    try {
      const docs = await this.getDocumentLibraryItems(options);
      const signedByDocumentId = await this.getSignedUrlsForDocuments(docs, 3600);

      return sortLibraryDocuments(docs).map(doc => this.formatDocumentForDisplay(
        doc,
        signedByDocumentId.get(doc.id) || null
      ));
    } catch (error) {
      console.error('getAllDocumentsWithFiles error:', error);
      if (options.throwOnError) throw error;
      return [];
    }
  }

  // Get signed URL for downloading a document
  async getDocumentFile(documentId) {
    try {
      const { data: doc, error } = await supabase
        .from('documents')
        .select('file_url')
        .eq('id', documentId)
        .maybeSingle();

      if (error || !doc) return null;

      // Seeded/legacy records have null or 'pending' file_url — no file in Storage
      if (!doc.file_url || doc.file_url === 'pending') return null;

      return await this.createSignedUrlForPath(doc.file_url, 3600);

    } catch (error) {
      console.error('getDocumentFile error:', error);
      return null;
    }
  }

  // Get single document with signed download URL
  async getDocumentWithFile(documentId) {
    try {
      const { data: doc, error } = await supabase
        .from('documents')
        .select('*')
        .eq('id', documentId)
        .maybeSingle();
      
      if (error || !doc) return null;
      
      const fileUrl = await this.getDocumentFile(documentId);
      return {
        ...doc,
        name: doc.title,
        size: doc.file_size_bytes ? (doc.file_size_bytes / (1024 * 1024)).toFixed(2) + ' MB' : '0 MB',
        type: doc.mime_type === 'application/pdf' ? 'PDF' : doc.mime_type?.startsWith('image/') ? 'Image' : 'Document',
        content: fileUrl,
      };
      
    } catch (error) {
      console.error('getDocumentWithFile error:', error);
      return null;
    }
  }

  // Update document metadata (used by ingestionValidator.approveLevel4)
  async updateDocumentMetadata(documentId, updates) {
    try {
      const mappedUpdates = { ...updates };
      // ai_status is its own column — do NOT remap it to validation_status.
      // validation_status only accepts: 'pending' | 'pass' | 'fail' | 'review'
      // ai_status only accepts: 'pending' | 'validating' | 'ready' | 'failed'

      let { data, error } = await supabase
        .from('documents')
        .update({ ...mappedUpdates, updated_at: new Date().toISOString() })
        .eq('id', documentId)
        .select()
        .single();

      if (error && isMissingSchemaError(error)) {
        const fallbackUpdates = stripOrderingFields(mappedUpdates);
        const fallback = await supabase
          .from('documents')
          .update({ ...fallbackUpdates, updated_at: new Date().toISOString() })
          .eq('id', documentId)
          .select()
          .single();
        data = fallback.data;
        error = fallback.error;
      }
      
      if (error) throw error;
      return data;
      
    } catch (error) {
      console.error('updateDocumentMetadata error:', error);
      throw error;
    }
  }

  async getDocumentFolders({ hoaId: requestedHoaId } = {}) {
    const hoaId = this.getHoaId(requestedHoaId);
    const { data, error } = await withSupabaseQueryTimeout(
      () => supabase
        .from('document_folders')
        .select('id, hoa_id, name, system_key, category_filter, sort_order, created_at, updated_at')
        .eq('hoa_id', hoaId)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      'Loading document folders',
      30000
    );

    if (error && isMissingSchemaError(error)) return [];
    if (error) throw error;
    return data || [];
  }

  async createDocumentFolder({ hoaId: requestedHoaId, name, createdBy }) {
    const hoaId = this.getHoaId(requestedHoaId);
    const folders = await this.getDocumentFolders({ hoaId });
    const maxOrder = folders.reduce((max, folder) => Math.max(max, folder.sort_order || 0), 0);
    const { data, error } = await supabase
      .from('document_folders')
      .insert({
        hoa_id: hoaId,
        name: name.trim(),
        sort_order: maxOrder + 10,
        created_by: createdBy || null,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateDocumentFolder(folderId, updates) {
    const allowedUpdates = {};
    if (updates.name !== undefined) allowedUpdates.name = updates.name.trim();
    if (updates.sort_order !== undefined) allowedUpdates.sort_order = updates.sort_order;

    const { data, error } = await supabase
      .from('document_folders')
      .update({ ...allowedUpdates, updated_at: new Date().toISOString() })
      .eq('id', folderId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteDocumentFolder(folderId) {
    const { count, error: countError } = await supabase
      .from('documents')
      .select('id', { count: 'exact', head: true })
      .eq('folder_id', folderId);

    if (countError) throw countError;
    if ((count || 0) > 0) {
      throw new Error('This folder contains documents. Move them to another folder before deleting it.');
    }

    const { error } = await supabase
      .from('document_folders')
      .delete()
      .eq('id', folderId);

    if (error) throw error;
    return true;
  }

  async updateDocumentOrder(orderedDocumentIds) {
    for (const [index, documentId] of orderedDocumentIds.entries()) {
      const { error } = await supabase
        .from('documents')
        .update({ display_order: index, updated_at: new Date().toISOString() })
        .eq('id', documentId);

      if (error) throw error;
    }
    return true;
  }

  async updateFolderOrder(orderedFolderIds) {
    for (const [index, folderId] of orderedFolderIds.entries()) {
      const { error } = await supabase
        .from('document_folders')
        .update({ sort_order: index * 10, updated_at: new Date().toISOString() })
        .eq('id', folderId);

      if (error) throw error;
    }
    return true;
  }

  // Delete document from Storage and database
  async deleteDocument(documentId) {
    // Get file path first
    const { data: doc } = await supabase
      .from('documents')
      .select('file_url')
      .eq('id', documentId)
      .single();
    
    // Attempt to remove from Storage (soft failure — file may not exist if record was seeded)
    if (doc?.file_url && doc.file_url !== 'pending') {
      try {
        await supabase.storage.from('documents').remove([doc.file_url]);
      } catch (storageErr) {
        console.warn('Storage removal skipped (file may not exist):', storageErr?.message);
      }
    }
    
    // Delete database row — hard failure: throw so the UI can report it
    const { error } = await supabase
      .from('documents')
      .delete()
      .eq('id', documentId);
    
    if (error) throw new Error(`Database delete failed: ${error.message}`);
    return true;
  }


  // Storage stats
  async getStorageStats(hoaId) {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select('file_size')
        .eq('hoa_id', this.getHoaId(hoaId));
      
      if (error) throw error;
      
      const totalBytes = (data || []).reduce((sum, d) => sum + (d.file_size || 0), 0);
      const formatSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      };
      
      return {
        totalDocuments: data?.length || 0,
        totalSize: formatSize(totalBytes),
        totalSizeBytes: totalBytes,
      };
      
    } catch (error) {
      console.error('getStorageStats error:', error);
      return { totalDocuments: 0, totalSize: '0 B', totalSizeBytes: 0 };
    }
  }
}

const documentStorage = new DocumentStorageService();
export default documentStorage;
export { DocumentStorageService };
